import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useClickOutside, useHover } from '@mantine/hooks'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { sortByName } from '@pubstack/common/src/sort'
import { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { useScopes } from '~/api/api.hook'
import { useUser } from '~/auth/user.hooks'
import { Colors } from '~/assets/style/colors'
import { ElevationLevel } from '~/assets/style/tokens'
import { ScrollbarStyle } from '~/assets/style/utils'
import { BANNER_PORTAL_ID } from '~/components/Banner'
import { FLYOUT_PORTAL_ID } from '~/components/Flyout'
import { UpliftNotFound } from '~/components/easter-egg/UpliftNotFound'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { GlobalModalWrapper } from '~/components/layout/GlobalModal'
import { Link } from '~/components/Link'
import { ProductIntegration } from '~/components/layout/ProductIntegration'
import { PureSidebar } from '~/components/layout/PureSidebar'
import { CommandMenu } from '~/components/layout/palette-command/CommandMenu'
import { Message } from '~/components/Message'
import { ToastWrapper } from '~/components/Toast/ToastWrapper'
import { configurationState, contextState, currentScopeState, sidebarOpenState, upliftAppliedState } from '~/state'
import { SUPPORT_MAIL } from '~/utils/constants'
import { BeamerIntegration } from './BeamerIntegration'
import { GlobalFlyoutWrapper } from './GlobalFlyout'
import Header from './header/Header'
import Menu from './menu/Menu'

/** Same shape the Forbidden page centres its message with, so the two dead ends read alike. */
const ErrorWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
`

const MailLink: FC<{ to: string }> = ({ to }) => <Link href={`mailto:${to}`} label={to} trackingLabel={'common-mail-to-support-navigate'} />

const StyledSidebar = styled(PureSidebar)`
  transition: all 0.3s ease-in-out;
  position: absolute;
  left: 0;
  top: 0;
  overflow-y: hidden;
  overflow-x: hidden;
  ${ElevationLevel.semiHigh};
`

const MenuWrapper = styled.div`
  grid-area: menu;
  position: relative;
`

const SidebarTrigger = styled.div`
  --size: 15px;
  min-width: var(--size);
  width: var(--size);
  min-height: 100%;
  height: 100%;
  background: transparent;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  ${ElevationLevel.semiHigh};
`

const Wrapper = styled.div<{ isSidebarOpen: boolean; isSidebarPinned: boolean }>`
  --menu: 196px;
  display: grid;
  grid-template-columns: var(--menu) 1fr;
  grid-template-rows: min-content min-content 1fr;
  grid-template-areas:
    'menu header'
    'menu messageBanner'
    'menu content';
  min-height: 100vh;
  max-height: 100vh;
  overflow: auto;
  transition: all 0.3s ease-in-out;
  position: relative;

  ${({ isSidebarOpen }) =>
    !isSidebarOpen &&
    css`
      --menu: 0px;
    `};

  ${({ isSidebarPinned }) =>
    !isSidebarPinned &&
    css`
      --menu: 0px;

      ${StyledSidebar} {
        top: 65px;
        height: calc(100% - 77px);
      }
    `};
`

const HeaderWrapper = styled.div`
  grid-area: header;
`

const ContentWrapper = styled.div`
  grid-area: content;
  background: ${Colors.Ghost};
  overflow: hidden;
  min-width: 1033px; // TODO 2022-06-07 cfo - UGLY ! It's 750px min width of content + min width 283px of filter sidebar ... To change when we work on mobile version
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: start;
`

const Content = styled.div`
  ${ScrollbarStyle};
  overflow: auto;
  flex-grow: 1;
`

const MessageBanner = styled(Message)`
  grid-area: messageBanner;
`

const Layout: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const user = useUser()
  const { all, byId } = useScopes()
  // `Array.isArray` because use-http hands back the body of a failed answer too.
  const scopes = useMemo(() => (Array.isArray(all.data) ? sortByName(all.data) : all.data), [all.data])
  const scope = byId.data
  const [currentScope, setCurrentScope] = useAtom(currentScopeState)
  const setContext = useSetAtom(contextState)
  const configuration = useAtomValue(configurationState)
  const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenState)
  const upliftApplied = useAtomValue(upliftAppliedState)
  const message = configuration.INFORMATION_BANNER
  const menuToggleRef = useRef<HTMLButtonElement>(null)

  const onCloseSidebar = () => {
    setSidebarOpen(() => ({ open: false, pinned: false }))
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  const onPinSidebar = () => {
    setSidebarOpen({ open: true, pinned: true })
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 400)
  }

  const sidebarRef = useClickOutside(
    () => {
      if (sidebarOpen.open && !sidebarOpen.pinned) {
        onCloseSidebar()
      }
    },
    null,
    // The click-outside API needs the toggle node; reading the ref here is required.
    // oxlint-disable-next-line react/refs
    menuToggleRef ? [menuToggleRef.current] : []
  )

  const { hovered: isMenuHover, ref: menuWrapperRef } = useHover()

  useEffect(() => {
    // `scope?.id`, not `scope`: use-http fills `data` from the response body whatever the status, so a
    // failed `/scopes/scope` hands back an error object here. Storing it would leave `currentScopeState`
    // with no id and nothing on screen.
    if (scope?.id) {
      setContext((context) => ({ ...context, timeRange: { ...context.timeRange, tz: scope.timezone } }))
      setCurrentScope(scope)
    }
  }, [scope])

  /* Easter egg: the header's `+30%` button takes the whole app with it. After every hook, so the
     hook order does not change when it flips. */
  if (upliftApplied) {
    return <UpliftNotFound />
  }

  if (!scopes) {
    return null
  }

  /* Two different things used to share one guard, and the common one is not an error.
     `currentScopeState` defaults to `{ id: '' }` and the effect above only fills it after the first
     commit, so `!currentScope.id` is true for one frame on *every* cold load — and both the scope
     swap and the role toggle end in `window.location.reload()`, which is exactly when someone is
     looking. That frame is a not-yet-synced state, so it returns null like `!scopes` does.

     The real failure is `!scope?.id`: what `GET /scopes/scope` actually answered. It is `?.id` and not
     a plain truthiness check because use-http assigns `data` from the response body without looking at
     the status, so a non-2xx answer arrives here as an error object — truthy, and carrying no id. That
     used to slip past this guard and fall through to the one below, which renders nothing.

     Both guards stay. ~20 call sites read `currentScopeState` and several key queries off the id,
     so rendering a frame with `id: ''` would trade a cosmetic flash for real query noise. */
  if (!scope?.id) {
    return (
      <Wrapper isSidebarOpen={false} isSidebarPinned={false}>
        <HeaderWrapper>
          <Header ref={menuToggleRef} scopes={scopes} />
        </HeaderWrapper>
        <ContentWrapper>
          <Content>
            <ErrorWrapper>
              <IllustrationMessage
                iconName={'lock_round'}
                iconSize={'190px'}
                title={'Current scope not found'}
                message={
                  <>
                    {/* The id is named when known because it is the one identifier a reader can hand to
                        support, and with no scope loaded there is nowhere else on screen it still
                        appears. The header keeps its picker on in exactly this state, so the
                        instruction is followable. */}
                    Please select a scope from the header, or contact <MailLink to={SUPPORT_MAIL} />
                    {user?.currentScopeId ? (
                      <>
                        {' '}
                        quoting scope <code>{user.currentScopeId}</code>
                      </>
                    ) : null}
                  </>
                }
              />
            </ErrorWrapper>
          </Content>
        </ContentWrapper>
      </Wrapper>
    )
  }

  if (!currentScope.id) {
    return null
  }

  return (
    <>
      <CommandMenu />
      {window.location.hostname !== 'localhost' ? <ProductIntegration scopes={scopes} /> : null}
      <BeamerIntegration scopes={scopes} />
      <Wrapper isSidebarOpen={sidebarOpen.open || isMenuHover} isSidebarPinned={sidebarOpen.pinned}>
        <HeaderWrapper>
          <Header ref={menuToggleRef} scopes={scopes} />
        </HeaderWrapper>
        <MenuWrapper ref={menuWrapperRef}>
          <SidebarTrigger />
          <StyledSidebar isOpen={sidebarOpen.open || isMenuHover} onOpen={onCloseSidebar} isPinned={sidebarOpen.pinned} onPin={onPinSidebar} ref={sidebarRef}>
            <Menu />
          </StyledSidebar>
        </MenuWrapper>
        {message && <MessageBanner status={message.level} title={message.title} description={message.description} />}
        <ContentWrapper id={FLYOUT_PORTAL_ID}>
          <ToastWrapper />
          <GlobalModalWrapper>
            <GlobalFlyoutWrapper>
              <Content id="PureLayoutContent">{children}</Content>
            </GlobalFlyoutWrapper>
          </GlobalModalWrapper>
          <div id={BANNER_PORTAL_ID} />
        </ContentWrapper>
      </Wrapper>
    </>
  )
}

export default Layout
